import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { roundToDecimalPlaces } from '@semios/app-platform-common'
import type { TEmitterType } from '@semios/app-platform-values-helpers-and-types'
import { computeDailyIrrigation } from 'App/irrigation-scheduler/components/Grid/components/IrrigationZoneEmitterRows/components/_utils/computeDailyIrrigation'
import moment from 'moment-timezone'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import type { TDateRange, TIrrigationPlanData } from '../types'
import { buildDateGrid } from './buildDateGrid'
import type { TParsedZoneEvents } from './parseZoneEvents'
import { getZoneEvents } from './parseZoneEvents'

export const buildScheduleData = (
  eventsResponse: routes.IrrigationSchedulerEventsGet.Response | null,
  irrigationZoneEmitters: TProperty['irrigationZoneEmitters'],
  dateRange: TDateRange,
  timezone: string,
  parsedEvents: Record<string, TParsedZoneEvents>,
): TIrrigationPlanData[] => {
  if (!eventsResponse?.length) return []

  const dates = buildDateGrid(dateRange, timezone)
  const days = dates.map((d) => moment.tz(d, timezone))
  const emitterZones: Partial<Record<TEmitterType, Set<number>>> = {}

  eventsResponse.forEach((ze) => {
    const set = emitterZones[ze.emitterType] ?? new Set<number>()

    set.add(ze.irrigationZoneId)

    emitterZones[ze.emitterType] = set
  })

  return (Object.entries(emitterZones) as [TEmitterType, Set<number>][]).map(([emitterType, zoneIds]) => {
    const zones: TIrrigationPlanData['zones'] = {}

    zoneIds.forEach((zoneId) => {
      const zoneEmitterKey = `${zoneId}|||${emitterType}` as keyof typeof irrigationZoneEmitters
      const mmPerHour = irrigationZoneEmitters?.[zoneEmitterKey]?.flowRate?.mmPerHour ?? 0
      const { scheduledEvents, scheduledPulseEvents } = getZoneEvents(parsedEvents, zoneEmitterKey)
      const dailyIrrigation = computeDailyIrrigation(days, scheduledEvents, scheduledPulseEvents, mmPerHour)

      const dailyEvents = dates.map((date, i) => ({
        date,
        duration: roundToDecimalPlaces(dailyIrrigation[i].duration, 2) ?? 0,
        applied: roundToDecimalPlaces(dailyIrrigation[i].depthMm, 4) ?? 0,
      }))

      zones[String(zoneId)] = { zoneId, dailyEvents }
    })

    return { emitterType, zones }
  })
}
